import { Link } from 'react-router-dom';
import { Heart, Users, Megaphone, ArrowRight, Award, ExternalLink } from 'lucide-react';

const INVOLVED_IMAGE = "https://customer-assets.emergentagent.com/job_protect-play-grow/artifacts/1vxb7f1f_AdobeStock_434474639.jpeg";

export const GetInvolved = () => {
  return (
    <div className="pt-20" data-testid="get-involved-page">
      {/* Intro Section */}
      <section className="py-20 md:py-32" data-testid="involved-intro">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <p className="text-sm uppercase tracking-[0.2em] font-bold text-[#5B4FF9] mb-4">
                Get Involved
              </p>
              <h1 className="text-4xl sm:text-5xl tracking-tight font-extrabold text-[#272926] leading-tight mb-8">
                Be part of the movement
              </h1>
              <p className="text-lg leading-relaxed text-zinc-600">
                Creating safer, more accessible youth sports requires a collective effort.
              </p>
            </div>
            <div className="relative">
              <img 
                src={INVOLVED_IMAGE} 
                alt="Community involvement in youth sports"
                className="rounded-3xl shadow-xl w-full h-auto"
              />
              <div className="absolute -bottom-6 -right-6 w-24 h-24 bg-amber-400 rounded-2xl -z-10"></div>
            </div>
          </div>
        </div>
      </section>

      {/* Ways to Help */}
      <section className="py-20 md:py-32 bg-zinc-50" data-testid="ways-to-help">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="text-center mb-16">
            <p className="text-sm uppercase tracking-[0.2em] font-bold text-[#5B4FF9] mb-4">
              Make An Impact
            </p>
            <h2 className="text-3xl sm:text-4xl tracking-tight font-bold text-[#272926] leading-snug">
              Ways to help
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {/* Donate */}
            <div className="bg-[#5B4FF9] rounded-3xl p-8 text-white" data-testid="way-donate">
              <div className="w-14 h-14 bg-white/20 rounded-2xl flex items-center justify-center mb-6">
                <Heart className="h-7 w-7 text-white" />
              </div>
              <h3 className="text-2xl font-bold mb-4">Donate</h3>
              <p className="text-[#E5E3FF] leading-relaxed mb-8">
                Support programs that expand access and improve safety for young athletes.
              </p>
              <a
                href="https://givebutter.com/players-health-foundation-website-button-suv1ls"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center w-full bg-white text-[#5B4FF9] rounded-full px-6 py-3 font-semibold transition-all transform hover:-translate-y-1 hover:shadow-lg"
                data-testid="donate-btn"
              >
                Make a Donation
                <ExternalLink className="ml-2 h-4 w-4" />
              </a>
            </div>

            {/* Apply for Grants */}
            <div className="bg-white border border-zinc-200 rounded-3xl p-8 shadow-sm" data-testid="way-grants">
              <div className="w-14 h-14 bg-[#F5F4FF] rounded-2xl flex items-center justify-center mb-6">
                <Award className="h-7 w-7 text-[#5B4FF9]" />
              </div>
              <h3 className="text-2xl font-bold text-[#272926] mb-4">Apply for Grants</h3>
              <p className="text-zinc-600 leading-relaxed mb-8">
                Youth sports organizations can apply for funding and safety resources.
              </p>
              <Link
                to="/grants"
                className="inline-flex items-center justify-center w-full bg-[#5B4FF9] text-white rounded-full px-6 py-3 font-semibold transition-all transform hover:-translate-y-1 hover:shadow-lg hover:bg-[#4A3FE8]"
                data-testid="grants-btn"
              >
                Learn About Grants
                <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </div>

            {/* Partner */}
            <div className="bg-white border border-zinc-200 rounded-3xl p-8 shadow-sm" data-testid="way-partner">
              <div className="w-14 h-14 bg-[#F5F4FF] rounded-2xl flex items-center justify-center mb-6">
                <Users className="h-7 w-7 text-[#5B4FF9]" />
              </div>
              <h3 className="text-2xl font-bold text-[#272926] mb-4">Partner</h3>
              <p className="text-zinc-600 leading-relaxed mb-8">
                Collaborate with us to bring resources and opportunities to communities.
              </p>
              <Link
                to="/contact"
                className="inline-flex items-center justify-center w-full bg-[#5B4FF9] text-white rounded-full px-6 py-3 font-semibold transition-all transform hover:-translate-y-1 hover:shadow-lg hover:bg-[#4A3FE8]"
                data-testid="partner-btn"
              >
                Become a Partner
                <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </div>

            {/* Spread the Word */}
            <div className="bg-[#272926] rounded-3xl p-8 text-white" data-testid="way-spread">
              <div className="w-14 h-14 bg-white/20 rounded-2xl flex items-center justify-center mb-6">
                <Megaphone className="h-7 w-7 text-white" />
              </div>
              <h3 className="text-2xl font-bold mb-4">Spread the Word</h3>
              <p className="text-zinc-300 leading-relaxed mb-8">
                Help us raise awareness and advocate for safer, more inclusive sports.
              </p>
              <button
                onClick={() => {
                  if (navigator.share) {
                    navigator.share({
                      title: 'Players Health Foundation',
                      text: 'Join the movement for safer, more accessible youth sports!',
                      url: window.location.origin,
                    });
                  } else {
                    navigator.clipboard.writeText(window.location.origin);
                    alert('Link copied to clipboard!');
                  }
                }}
                className="inline-flex items-center justify-center w-full bg-white text-[#272926] rounded-full px-6 py-3 font-semibold transition-all transform hover:-translate-y-1 hover:shadow-lg"
                data-testid="share-btn"
              >
                Share Our Mission
                <Megaphone className="ml-2 h-4 w-4" />
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 md:py-32 bg-[#F5F4FF]" data-testid="involved-cta">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl sm:text-4xl tracking-tight font-bold text-[#272926] leading-snug mb-6">
              Get involved today
            </h2>
            <p className="text-lg leading-relaxed text-zinc-600 mb-8">
              Every contribution—whether time, resources, or advocacy—helps us create a safer, more accessible future for youth athletes.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="https://givebutter.com/players-health-foundation-website-button-suv1ls"
                target="_blank"
                rel="noopener noreferrer"
                className="btn-primary inline-flex items-center justify-center"
                data-testid="involved-donate-btn"
              >
                Donate Now
                <ExternalLink className="ml-2 h-5 w-5" />
              </a>
              <Link
                to="/contact"
                className="btn-secondary inline-flex items-center justify-center"
                data-testid="involved-contact-btn"
              >
                Contact Us
                <ArrowRight className="ml-2 h-5 w-5" />
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};
